import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Check, X, Loader2, Clock, FileText, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import type { Json } from '@/integrations/supabase/types';

interface Submission {
  id: string;
  template_id: string;
  user_id: string;
  status: string;
  admin_notes: string | null;
  reviewed_by: string | null;
  created_at: string;
  reviewed_at: string | null;
  template_name: string;
  template_snapshot: Record<string, unknown>;
  username?: string;
}

export const TemplateSubmissions: React.FC = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('template_submissions')
      .select('*')
      .order('created_at', { ascending: false });

    if (filter === 'pending') {
      query = query.eq('status', 'pending');
    }

    const { data } = await query;
    
    if (data) {
      // Get usernames
      const userIds = [...new Set(data.map(s => s.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, username')
        .in('user_id', userIds);
      const usernameMap = new Map((profiles || []).map(p => [p.user_id, p.username]));

      setSubmissions(data.map(s => ({
        ...s,
        template_snapshot: (s.template_snapshot as Record<string, unknown>) || {},
        username: usernameMap.get(s.user_id) || 'Unknown',
      })));
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleReview = (submission: Submission) => {
    setSelectedSubmission(submission);
    setAdminNotes(submission.admin_notes || '');
    setReviewDialogOpen(true);
  };

  const handleAction = async (action: 'approved' | 'rejected') => {
    if (!selectedSubmission || !user) return;
    setProcessing(true);

    try {
      // Update submission status
      const { error: updateError } = await supabase
        .from('template_submissions')
        .update({
          status: action,
          admin_notes: adminNotes || null,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', selectedSubmission.id);

      if (updateError) throw updateError;

      // If approved, make the template global
      if (action === 'approved') {
        const { error: templateError } = await supabase
          .from('templates')
          .update({ is_global: true })
          .eq('id', selectedSubmission.template_id);

        if (templateError) {
          console.error('Failed to make template global:', templateError);
          toast.warning('Submission approved but failed to make template global');
        }
      }

      toast.success(action === 'approved' ? 'Template approved and made global' : 'Submission rejected');
      setReviewDialogOpen(false);
      fetchSubmissions();
    } catch (e) {
      toast.error('Failed to process submission');
    } finally {
      setProcessing(false);
    }
  };

  const pendingCount = submissions.filter(s => s.status === 'pending').length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Template Submissions
              {pendingCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {pendingCount} pending
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Review user-submitted templates for global publication
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={filter === 'pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('pending')}
            >
              Pending
            </Button>
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              All
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No {filter === 'pending' ? 'pending ' : ''}submissions</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {submissions.map(sub => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{sub.template_name}</span>
                      <Badge
                        variant={
                          sub.status === 'pending' ? 'secondary' :
                          sub.status === 'approved' ? 'default' : 'destructive'
                        }
                        className="text-xs"
                      >
                        {sub.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>by {sub.username}</span>
                      <span>•</span>
                      <Clock className="w-3 h-3" />
                      <span>{formatDistanceToNow(new Date(sub.created_at), { addSuffix: true })}</span>
                      {sub.admin_notes && (
                        <>
                          <span>•</span>
                          <MessageSquare className="w-3 h-3" />
                          <span className="truncate max-w-[200px]">{sub.admin_notes}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {sub.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => handleReview(sub)}>
                      Review
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review: {selectedSubmission?.template_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-sm text-muted-foreground">
              Submitted by <strong>{selectedSubmission?.username}</strong>{' '}
              {selectedSubmission && formatDistanceToNow(new Date(selectedSubmission.created_at), { addSuffix: true })}
            </div>
            <div className="space-y-2">
              <Label>Admin Notes (optional)</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add feedback or notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="destructive"
              onClick={() => handleAction('rejected')}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
              Reject
            </Button>
            <Button
              onClick={() => handleAction('approved')}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Approve & Make Global
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
